import { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './FeedbackPage.css';

// ─────────────────────────────────────────────
// EmailJS kurulumu için:
// 1. https://www.emailjs.com adresine git ve ücretsiz hesap aç
// 2. "Email Services" → Gmail bağla → Service ID'yi al
// 3. "Email Templates" → yeni şablon oluştur → Template ID'yi al
// 4. "Account" → Public Key'i al
// Aşağıdaki 3 sabiti kendi değerlerinle doldur:
// ─────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = 'service_yawk46r';
const EMAILJS_TEMPLATE_ID = 'template_phrn3gc';
const EMAILJS_PUBLIC_KEY  = 'ONybTkfWcBMS_kk4G';

const categories = [
  { value: '', label: 'Bir kategori seçin' },
  { value: 'Hata bildirimi', label: 'Hata bildirimi' },
  { value: 'Öneri', label: 'Öneri' },
  { value: 'Hesap sorunu', label: 'Hesap sorunu' },
  { value: 'Not yükleme sorunu', label: 'Not yükleme sorunu' },
  { value: 'Bir notu veya kullanıcıyı bildir', label: 'Bir notu veya kullanıcıyı bildir' },
  { value: 'Diğer', label: 'Diğer' },
];

const STATUS = { IDLE: 'idle', LOADING: 'loading', SUCCESS: 'success', ERROR: 'error' };

export default function FeedbackPage() {
  const formRef = useRef();
  const [status, setStatus] = useState(STATUS.IDLE);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: '', email: '', category: '', message: '',
  });

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = 'Lütfen e-posta adresinizi girin.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = 'Geçerli bir e-posta adresi girin.';
    }
    if (!form.category) e.category = 'Lütfen bir kategori seçin.';
    if (!form.message.trim()) e.message = 'Lütfen mesajınızı yazın.';
    else if (form.message.trim().length < 10) e.message = 'Mesaj en az 10 karakter olmalıdır.';
    return e;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus(STATUS.LOADING);
    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY,
      );
      setStatus(STATUS.SUCCESS);
      setForm({ name: '', email: '', category: '', message: '' });
    } catch {
      setStatus(STATUS.ERROR);
    }
  };

  return (
    <div className="feedback-page">
      {/* Hero */}
      <section className="fb-hero">
        <div className="container">
          <div className="fb-hero__tag">İletişim</div>
          <h1 className="fb-hero__title">Geri Bildirim</h1>
          <p className="fb-hero__subtitle">
            Sizden haber almak isteriz. Geri bildirimlerinizi, önerilerinizi gönderin veya bir sorunu bildirin.
          </p>
        </div>
      </section>

      {/* Form + Contact */}
      <section className="fb-main">
        <div className="container">
          <div className="fb-grid">

            {/* Form */}
            <div className="fb-form-wrap">
              {status === STATUS.SUCCESS ? (
                <div className="fb-success">
                  <div className="fb-success__icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <circle cx="24" cy="24" r="22" fill="#E8F5E9"/>
                      <path d="M14 24l8 8 14-14" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Mesajınız iletildi!</h3>
                  <p>Geri bildiriminiz için teşekkür ederiz. En kısa sürede dönüş yapacağız.</p>
                  <button onClick={() => setStatus(STATUS.IDLE)} className="fb-success__btn">
                    Yeni mesaj gönder
                  </button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} noValidate className="fb-form">
                  <h2 className="fb-form__title">Mesaj Gönder</h2>

                  {/* İsim */}
                  <div className="fb-field">
                    <label className="fb-label">
                      İsim <span className="fb-label__opt">(opsiyonel)</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Adınız"
                      className="fb-input"
                    />
                  </div>

                  {/* E-posta */}
                  <div className="fb-field">
                    <label className="fb-label">
                      E-posta <span className="fb-label__req">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="eposta@ornek.com"
                      className={`fb-input${errors.email ? ' fb-input--error' : ''}`}
                      required
                    />
                    {errors.email && <p className="fb-error">{errors.email}</p>}
                  </div>

                  {/* Kategori */}
                  <div className="fb-field">
                    <label className="fb-label">
                      Kategori <span className="fb-label__req">*</span>
                    </label>
                    <div className="fb-select-wrap">
                      <select
                        name="category"
                        value={form.category}
                        onChange={handleChange}
                        className={`fb-select${errors.category ? ' fb-input--error' : ''}`}
                      >
                        {categories.map(c => (
                          <option key={c.value} value={c.value} disabled={c.value === ''}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <span className="fb-select-arrow">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M4 6l4 4 4-4" stroke="#5784E6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </div>
                    {errors.category && <p className="fb-error">{errors.category}</p>}
                  </div>

                  {/* Mesaj */}
                  <div className="fb-field">
                    <label className="fb-label">
                      Mesaj <span className="fb-label__req">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Mesajınızı buraya yazın..."
                      rows={5}
                      className={`fb-textarea${errors.message ? ' fb-input--error' : ''}`}
                    />
                    {errors.message && <p className="fb-error">{errors.message}</p>}
                  </div>

                  {status === STATUS.ERROR && (
                    <div className="fb-alert">
                      Mesaj gönderilemedi. Lütfen tekrar deneyin veya doğrudan{' '}
                      <a href="mailto:noteswapp@gmail.com">noteswapp@gmail.com</a> adresine yazın.
                    </div>
                  )}

                  <button
                    type="submit"
                    className="fb-submit"
                    disabled={status === STATUS.LOADING}
                  >
                    {status === STATUS.LOADING ? (
                      <span className="fb-submit__spinner" />
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M2 9l13-6-6 13-2-5-5-2z" fill="white"/>
                        </svg>
                        Geri Bildirim Gönder
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="fb-sidebar">
              <h3 className="fb-sidebar__title">Bize Ulaşmanın Diğer Yolları</h3>
              <p className="fb-sidebar__desc">
                Aşağıdaki yöntemlerle de bize doğrudan ulaşabilirsiniz.
              </p>

              <div className="fb-contact-cards">
                <div className="fb-contact-card">
                  <div className="fb-contact-card__icon">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <rect x="2" y="5" width="18" height="13" rx="3" stroke="#5784E6" strokeWidth="1.8"/>
                      <path d="M2 8l9 5.5L20 8" stroke="#5784E6" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="fb-contact-card__label">E-posta</p>
                    <a href="mailto:noteswapp@gmail.com" className="fb-contact-card__value">
                      noteswapp@gmail.com
                    </a>
                  </div>
                </div>

                <div className="fb-contact-card">
                  <div className="fb-contact-card__icon">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <circle cx="11" cy="11" r="9" stroke="#5784E6" strokeWidth="1.8"/>
                      <path d="M11 7v4l3 2" stroke="#5784E6" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="fb-contact-card__label">Yanıt süresi</p>
                    <p className="fb-contact-card__value">Genellikle 1-3 iş günü</p>
                  </div>
                </div>

                <div className="fb-contact-card">
                  <div className="fb-contact-card__icon">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <path d="M11 2C7.13 2 4 5.13 4 9c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7z" stroke="#5784E6" strokeWidth="1.8"/>
                      <circle cx="11" cy="9" r="2.5" stroke="#5784E6" strokeWidth="1.8"/>
                    </svg>
                  </div>
                  <div>
                    <p className="fb-contact-card__label">Yardım merkezi</p>
                    <a href="/help" className="fb-contact-card__value">
                      SSS sayfasını incele →
                    </a>
                  </div>
                </div>
              </div>

              <div className="fb-note">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="#5784E6" strokeWidth="1.5"/>
                  <path d="M8 5v3M8 10v1" stroke="#5784E6" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <p>Tüm geri bildirimler <strong>noteswapp@gmail.com</strong> adresine iletilmektedir.</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
